import { useState, useCallback, useMemo, useRef, useEffect, useReducer } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useProjectStore } from '@/store/projectStore.ts';
import { useCommentStore } from '@/store/commentStore.ts';
import { useAccountStore } from '@/store/accountStore.ts';
import { useUiStore } from '@/store/uiStore.ts';
import { useMobileLayout } from '@/hooks/useMobileLayout.ts';
import { useLaptopSplitActive } from '@/platform/laptopSplitState.ts';
import { capabilities } from '@/capabilities.ts';
import type { Comment } from '@/types/timeline.ts';
import { getReviewerColor } from '@/utils/reviewerColor.ts';
import styles from './CommentPanel.module.css';

export function CommentPanel() {
  const { t } = useTranslation('comments');
  const commentPanelOpen = useUiStore(s => s.commentPanelOpen);
  const selectedShotId = useProjectStore(s => s.selectedShotId);

  const addComment = useCommentStore(s => s.addComment);
  const deleteComment = useCommentStore(s => s.deleteComment);
  const deleteAllCommentsForShot = useCommentStore(s => s.deleteAllCommentsForShot);

  const reviewerName = useCommentStore(s => s.reviewerName);
  const isCreator = useProjectStore(s => s.isCreator);
  const readOnly = useProjectStore(s => s.readOnly);
  const project = useProjectStore(s => s.project);
  useProjectStore(s => s.activeSceneId); // re-render trigger for scene changes
  // Subscribe to raw arrays so mutations trigger re-render
  const allComments = useCommentStore(s => s.comments);

  const [newText, setNewText] = useState('');
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyText, setReplyText] = useState('');
  const [copied, setCopied] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const prevCommentCount = useRef(0);


  const scene = useMemo(() => {
    if (selectedShotId === null || !project) return null;
    for (const sc of project.scenes) {
      if (sc.shots.some(s => s.id === selectedShotId)) return sc;
    }
    return null;
  }, [selectedShotId, project]);
  // Build set of all shot IDs in the active scene for scene-level filtering
  const sceneShotIds = useMemo(() => {
    if (!scene) return new Set<number>();
    return new Set(scene.shots.map(s => s.id));
  }, [scene]);

  // Shot ID → shot number lookup for display
  const shotNumberMap = useMemo(() => {
    if (!scene) return new Map<number, string>();
    const map = new Map<number, string>();
    for (const s of scene.shots) map.set(s.id, String(s.number));
    return map;
  }, [scene]);

  // Filter comments by scene (all shots in the scene) instead of single shot
  const comments = useMemo(() =>
    sceneShotIds.size > 0 ? allComments.filter(c => c.shotId !== null && sceneShotIds.has(c.shotId)) : [],
    [allComments, sceneShotIds]
  );

  // All hooks MUST be above any conditional returns
  const topLevel = comments.filter(c => !c.parentCommentId);

  const repliesMap = useMemo(() => {
    const map = new Map<number, Comment[]>();
    for (const c of comments) {
      if (c.parentCommentId) {
        const arr = map.get(c.parentCommentId) ?? [];
        arr.push(c);
        map.set(c.parentCommentId, arr);
      }
    }
    return map;
  }, [comments]);

  const handleSubmit = useCallback(() => {
    if (!newText.trim() || selectedShotId === null) return;
    addComment(selectedShotId, null, newText.trim());
    setNewText('');
  }, [newText, selectedShotId, addComment]);

  const handleReply = useCallback(() => {
    if (!replyText.trim() || replyingTo === null || selectedShotId === null) return;
    addComment(selectedShotId, null, replyText.trim(), replyingTo);
    setReplyText('');
    setReplyingTo(null);
  }, [replyText, replyingTo, selectedShotId, addComment]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent, submitFn: () => void) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitFn();
    }
  }, []);

  // Sync reviewer name from OAuth profile on sign-in
  const accountUser = useAccountStore(s => s.accountUser);
  useEffect(() => {
    if (accountUser?.displayName && accountUser.displayName !== reviewerName) {
      useCommentStore.getState().setReviewerName(accountUser.displayName);
    }
  }, [accountUser?.displayName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-render every 30s so "time ago" labels stay fresh
  const [, tick] = useReducer(n => n + 1, 0);
  useEffect(() => {
    if (!commentPanelOpen || comments.length === 0) return;
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, [commentPanelOpen, comments.length]);

  // Auto-focus textarea when panel opens
  useEffect(() => {
    if (!commentPanelOpen || selectedShotId === null) return;
    const id = requestAnimationFrame(() => {
      textareaRef.current?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [commentPanelOpen, selectedShotId]);

  // Auto-scroll to bottom when a comment is added (local post or remote arrival)
  useEffect(() => {
    if (comments.length > prevCommentCount.current && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
    prevCommentCount.current = comments.length;
  }, [comments.length]);

  const isMobile = useMobileLayout();
  // 20260929 - the laptop split's bottom region is too short for a side panel (owner: "make the
  // Script/Comments panels fullscreen"), so there the panel is the phone's fullscreen overlay too: the
  // stylesheet's `:global(.laptop-split)` selector does the layout; this does the portal and brings the
  // overlay's own close button (the TopHeader toggle is under the overlay then).
  const laptopSplitActive = useLaptopSplitActive();
  const fullscreenOverlay = isMobile || laptopSplitActive;
  const toggleCommentPanel = useUiStore(s => s.toggleCommentPanel);

  const handleCopyAll = useCallback(() => {
    if (comments.length === 0) return;
    const label = t('clipboardCopySceneLabel', { sceneNumber: scene?.number || '?' });
    const lines = comments.map(c => {
      const sn = c.shotId !== null ? shotNumberMap.get(c.shotId) : undefined;
      return `${c.author}${sn ? ` [Shot ${sn}]` : ''}: ${c.text}`;
    });
    navigator.clipboard.writeText(`${label}\n${lines.join('\n')}`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [comments, scene?.number, shotNumberMap, t]);

  const handleDeleteAll = useCallback(() => {
    if (comments.length === 0) return;
    for (const shotId of sceneShotIds) deleteAllCommentsForShot(shotId);
  }, [comments.length, sceneShotIds, deleteAllCommentsForShot]);

  // Now safe to do conditional return — all hooks called above
  if (!commentPanelOpen || selectedShotId === null) return null;

  const content = (
    <div className={`${styles.panel} ${readOnly ? styles.panelReview : ''}`}>
      {/* Header */}
      <div className={styles.header}>
        <span className={styles.headerTitle}>
          {t('headerTitleScene', { sceneNumber: scene?.number || '?' })}
          <span className={styles.headerComments}>{t('headerCommentsLabel')}</span>
        </span>
        {comments.length > 0 && (
          <>
            <button
              className={styles.copyAllBtn}
              onClick={handleCopyAll}
              title={copied ? t('copiedTitle') : t('copyAllCommentsTitle')}
            >
              {copied ? (
                <span className={styles.copiedLabel}>{t('copiedLabel')}</span>
              ) : (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2-2v1" />
                </svg>
              )}
            </button>
            {capabilities.bridge && (
              <button
                className={styles.copyAllBtn}
                onClick={handleDeleteAll}
                title={t('deleteAllCommentsTitle')}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path d="M10 11v6" />
                  <path d="M14 11v6" />
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                </svg>
              </button>
            )}
          </>
        )}
        <span className={styles.reviewerBadge}>{reviewerName}</span>
        {fullscreenOverlay && (
          <button className={styles.closeBtn} onClick={toggleCommentPanel}>&times;</button>
        )}
      </div>


      {/* Comment List */}
      <div className={styles.commentList} ref={listRef}>
        {topLevel.map(comment => (
          <CommentThread
            key={comment.id}
            comment={comment}
            replies={repliesMap.get(comment.id) ?? []}
            canDelete={isCreator || comment.author === reviewerName}
            replyingTo={replyingTo}
            replyText={replyText}
            shotNumberMap={shotNumberMap}
            onSetReplyingTo={setReplyingTo}
            onSetReplyText={setReplyText}
            onReply={handleReply}
            onDelete={deleteComment}
            onKeyDown={handleKeyDown}
          />
        ))}
      </div>

      {/* Add Comment Form */}
      <div className={styles.addForm}>
        <textarea
          ref={textareaRef}
          className={styles.textarea}
          value={newText}
          onChange={e => setNewText(e.target.value)}
          onKeyDown={e => handleKeyDown(e, handleSubmit)}
          placeholder={t('addCommentPlaceholder')}
          rows={2}
        />
        <button
          className={styles.submitBtn}
          onClick={handleSubmit}
          disabled={!newText.trim()}
        >
          {t('sendButton')}
        </button>
      </div>
    </div>
  );

  // Portal to body as the fullscreen overlay so the panel escapes viewerWrapper's stacking context
  return fullscreenOverlay ? createPortal(content, document.body) : content;
}

// ── Comment Thread ──────────────────────────────────────────────────
interface CommentThreadProps {
  comment: Comment;
  replies: Comment[];
  canDelete: boolean;
  replyingTo: number | null;
  replyText: string;
  shotNumberMap: Map<number, string>;
  onSetReplyingTo: (id: number | null) => void;
  onSetReplyText: (text: string) => void;
  onReply: () => void;
  onDelete: (id: number) => void;
  onKeyDown: (e: React.KeyboardEvent, submitFn: () => void) => void;
}

function CommentThread({
  comment, replies, canDelete, replyingTo, replyText, shotNumberMap,
  onSetReplyingTo, onSetReplyText, onReply,
  onDelete, onKeyDown,
}: CommentThreadProps) {
  const { t } = useTranslation('comments');
  return (
    <div className={styles.thread}>
      <CommentItem comment={comment} shotNumberMap={shotNumberMap} />
      <div className={styles.actions}>
        <button className={styles.actionBtn} onClick={() => onSetReplyingTo(replyingTo === comment.id ? null : comment.id)}>
          {t('replyButton')}
        </button>
        {canDelete && (
          <button className={styles.actionBtn} onClick={() => onDelete(comment.id)}>
            {t('deleteButton')}
          </button>
        )}
      </div>

      {/* Replies */}
      {replies.length > 0 && (
        <div className={styles.replies}>
          {replies.map(r => (
            <CommentItem key={r.id} comment={r} shotNumberMap={shotNumberMap} />
          ))}
        </div>
      )}

      {/* Reply input */}
      {replyingTo === comment.id && (
        <div className={styles.replyForm}>
          <textarea
            className={styles.textarea}
            value={replyText}
            onChange={e => onSetReplyText(e.target.value)}
            onKeyDown={e => onKeyDown(e, onReply)}
            placeholder={t('replyPlaceholder')}
            rows={1}
            autoFocus
          />
          <button className={styles.submitBtn} onClick={onReply} disabled={!replyText.trim()}>
            {t('replyButton')}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Single Comment ──────────────────────────────────────────────────
function CommentItem({ comment, shotNumberMap }: { comment: Comment; shotNumberMap: Map<number, string> }) {
  const { t } = useTranslation('comments');
  const isCreator = useProjectStore(s => s.isCreator);
  const reviewerName = useCommentStore(s => s.reviewerName);
  const initial = (comment.author || 'A')[0].toUpperCase();
  const timeAgo = formatTimeAgo(comment.createdAt, t);
  const fullTime = formatFullTime(comment.createdAt);
  const tc = comment.timecode != null ? formatTimecode(comment.timecode) : null;
  const shotNumber = comment.shotId !== null ? shotNumberMap.get(comment.shotId) : undefined;

  // Show creator badge if isCreator toggle is on and this comment is by the current user
  const isOwnComment = comment.author === reviewerName;
  const showCreatorBadge = isCreator && isOwnComment;

  const hasAnnotation = !!comment.annotationSvg;
  const authorColor = getReviewerColor(comment.author);

  return (
    <div className={styles.commentItem}>
      <div className={styles.avatar} style={{ background: authorColor }}>{initial}</div>
      <div className={styles.commentBody}>
        <div className={styles.commentMeta}>
          <span className={showCreatorBadge ? styles.creatorAuthor : styles.author} style={{ color: authorColor }}>
            {comment.author}
          </span>
          {showCreatorBadge && <span className={styles.creatorBadge}>{t('creatorBadge')}</span>}
          {shotNumber && <span className={styles.shotLabel}>{t('shotLabel', { shotNumber })}</span>}
          {hasAnnotation && (
            <span
              className={styles.annotationBadge}
              title={t('hasDrawingAnnotationTitle')}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
              </svg>
            </span>
          )}
          {tc && <span className={styles.timecode}>{tc}</span>}
          <span className={styles.time} title={fullTime}>{timeAgo}</span>
        </div>
        <div className={styles.commentText}>{comment.text}</div>
      </div>
    </div>
  );
}

function formatTimecode(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  const f = Math.round((seconds % 1) * 24); // 24fps frame count
  return `${m}:${s.toString().padStart(2, '0')}:${f.toString().padStart(2, '0')}`;
}

function formatTimeAgo(isoString: string, t: (key: string, options?: Record<string, unknown>) => string): string {
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '';
  const diff = Date.now() - date.getTime();
  if (diff < 0) return t('timeAgoJustNow'); // future timestamps (clock skew)
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t('timeAgoJustNow');
  if (mins < 60) return t('timeAgoMinutes', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('timeAgoHours', { count: hours });
  const days = Math.floor(hours / 24);
  return t('timeAgoDays', { count: days });
}

function formatFullTime(isoString: string): string {
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}
